import React, { useState, useEffect } from 'react';
import { useForm, usePage } from '@inertiajs/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, X } from 'lucide-react';

const ChatModal = ({ isOpen, onClose, facility, initialMessage = '' }) => {
  // Get current user from Inertia shared props
  const { current_user } = usePage().props;
  const [step, setStep] = useState('message'); // message, login, signup, success
  const [emailExists, setEmailExists] = useState(false);
  
  const { data, setData, post, processing, errors, reset } = useForm({
    email: current_user?.email || '',
    message: initialMessage,
    password: '',
    facility_id: facility?.id || '',
  });
  
  // Update message when initialMessage changes
  useEffect(() => {
    if (initialMessage) {
      setData('message', initialMessage);
    }
  }, [initialMessage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (step === 'message') {
      // Submit the initial message
      post('/conversations', {
        onSuccess: (page) => {
          // Check if we got a direct page response (user was logged in)
          if (page && typeof page === 'object' && page.component) {
            // We were redirected to the conversation page
            onClose();
            return;
          }
          
          // Check for JSON response with status
          const response = page?.props?.flash?.message || page;
          
          if (response?.status === 'existing_user') {
            setEmailExists(true);
            setStep('login');
          } else if (response?.status === 'new_user') {
            setStep('success');
          } else {
            // Default success case
            setStep('success');
          }
        },
        onError: (errors) => {
          console.error('Submission errors:', errors);
        }
      });
    } else if (step === 'login') {
      // Handle login
      post('/sign_in', {
        onSuccess: () => {
          // After login, create the conversation with the pending message
          post('/conversations', {
            onSuccess: () => {
              onClose();
            },
            onError: (errors) => {
              console.error('Conversation creation errors:', errors);
            }
          });
        },
        onError: (errors) => {
          console.error('Login errors:', errors);
        }
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Only allow closing via the explicit close button or ESC key
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            {step === 'message' && `Contact ${facility?.name || 'Community'}`}
            {step === 'login' && 'Sign In to Continue'}
            {step === 'success' && 'Message Sent!'}
          </DialogTitle>
          <DialogDescription>
            {step === 'message' && 'Send a message to this community to learn more about their services and availability.'}
            {step === 'login' && 'We found an account with this email. Please sign in to continue.'}
            {step === 'success' && 'Your message has been sent successfully. The community will respond to you shortly.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'message' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!current_user && (
              <div className="grid gap-2">
                <Label htmlFor="email">Your Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={data.email}
                  onChange={e => setData('email', e.target.value)}
                  placeholder="email@example.com"
                  required
                />
                {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              </div>
            )}
            
            <div className="grid gap-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                name="message"
                rows={4}
                value={data.message}
                onChange={e => setData('message', e.target.value)}
                placeholder={`Ask ${facility?.name || 'this community'} about availability, pricing, or anything else you'd like to know.`}
                required
              />
              {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
            </div>
            
            <DialogFooter className="mt-4">
              <Button
                type="submit"
                disabled={processing}
                className="w-full"
              >
                {processing ? 'Sending...' : 'Send Message'}
              </Button>
            </DialogFooter>
            
            <p className="text-xs text-muted-foreground mt-2">
              By sending a message, you agree to our Terms of Service and Privacy Policy.
              We'll create an account for you to keep track of your conversations.
            </p>
          </form>
        )}

        {step === 'login' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                We found an existing account with this email. Please sign in to continue your conversation.
              </p>
              <input
                type="hidden"
                name="email"
                value={data.email}
              />
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  value={data.password}
                  onChange={e => setData('password', e.target.value)}
                  required
                />
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>
            </div>
            
            <DialogFooter className="mt-4">
              <Button
                type="submit"
                disabled={processing}
                className="w-full"
              >
                {processing ? 'Signing in...' : 'Sign In & Send Message'}
              </Button>
            </DialogFooter>
            
            <div className="text-center">
              <a href="/identity/password_reset/new" className="text-sm text-primary hover:text-primary/80">
                Forgot your password?
              </a>
            </div>
          </form>
        )}

        {step === 'success' && (
          <div className="text-center py-6">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
              <CheckCircle className="h-6 w-6 text-primary" />
            </div>
            <p className="mt-4 text-lg font-medium">Message sent successfully!</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Please check your email to verify your account and continue the conversation.
            </p>
            <DialogFooter className="mt-6">
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChatModal;
